import axios from 'axios'
import { getAxiosConfig } from '@/utils/apiUtils'
import { errorCodes } from '@/utils/FormErrorCodes'
import { formatDateForOnlineRequest, formatDateTimeForOnlineRequest } from '@/utils/date'
import CommonApi from '@/api/commonApi'
import store from '@/store/index'

export default class {
  type = undefined

  constructor (type) {
    this.type = type
    this.commonApi = new CommonApi()
  }

  getApiEntry (apiRoute = '') {
    if (!process.env.VUE_APP_BACKEND_URL) {
      throw new Error('FATAL: you must define VUE_APP_BACKEND_URL and recompile front. (see .env file)')
    }
    return process.env.VUE_APP_BACKEND_URL.concat(apiRoute)
  }

  submitRequest (arg1, arg2, arg3, arg4) {
    let ret = null

    switch (this.type) {
      case 'create_account':
        ret = this.submitAccountCreationRequest(arg1, arg2)
        break
      case 'change_password':
        ret = this.submitNewPasswordRequest(arg1)
        break
      case 'change_hotel_password':
        ret = this.submitHotelNewPasswordRequest(arg1)
        break
      case 'add_company_domain':
        ret = this.submitCompanyAddDomain(arg1)
        break
      case 'change_company_logo':
        ret = this.submitCompanyLogo(arg1)
        break
      case 'traveller_add':
        ret = this.submitTravellerAdd(arg1, arg2)
        break
      case 'traveller_edit':
        ret = this.submitTravellerEdit(arg1)
        break
      case 'group':
        ret = this.submitGroupRequest(arg1)
        break
      case 'seminar':
        ret = this.submitSeminarRequest(arg1)
        break
      case 'survey':
        ret = this.submitSurvey(arg1, arg2)
        break
      case 'public_survey':
        ret = this.submitPublicSurvey(arg1, arg2)
        break
      case 'home_research':
        ret = this.submitHomeRequest(arg1, arg2)
        break
      case 'offline_research':
        ret = this.submitOfflineRequest(arg1)
        break
      case 'offline_research_duplicate':
        ret = this.submitOfflineRequestDuplicate(arg1)
        break
      case 'offline_booking_edit':
        ret = this.submitOfflineRequestEdit(arg1, arg2)
        break
      case 'public_offline_booking_edit':
        ret = this.submitPublicOfflineBookingEdit(arg1, arg2)
        break
      case 'pre_logged_search':
        ret = this.submitPreloggedRequest(arg1, arg2)
        break
      case 'generic_search':
        ret = this.submitGenericRequest(arg1, arg2)
        break
      case 'online_search':
        ret = this.submitOnlineRequest(arg1, arg2, arg3, arg4)
        break
      case 'hotel_detail_availabilities':
        ret = this.submitHotelDetailAvailabilitiesRequest(arg1)
        break
      case 'submit_online_booking':
        ret = this.submitOnlineBooking(arg1)
        break
      case 'online_validate':
        ret = this.validateOnlineBooking(arg1)
        break
      case 'booking_cancel':
        ret = this.submitBookingCancel(arg1, arg2)
        break
      case 'public_booking_cancel':
        ret = this.submitPublicBookingCancel(arg1, arg2)
        break
      case 'booking_deny':
        ret = this.submitDenyBooking(arg1, arg2)
        break
      case 'booking_allow':
        ret = this.submitAllowBooking(arg1, arg2)
        break
      case 'booking_confirm':
        ret = this.submitConfirmBooking(arg2)
        break
      case 'fields_white_mark':
        ret = this.submitWhiteMark(arg1, arg2)
        break
      case 'send_voucher':
        ret = this.submitSendVoucher(arg1, arg2)
        break
      case 'notify_travellers':
        ret = this.submitNotifyTravellers(arg2)
        break
      case 'order_number':
        ret = this.submitOrderNumber(arg1, arg2)
        break
      case 'resend_email':
        ret = this.submitResendEmail(arg1, arg2)
        break
      case 'delete_unfinished_booking':
        ret = this.softDeleteUnfinishedBooking(arg2)
        break
      case 'zone_add':
        ret = this.addZone(arg2)
        break
      case 'zone_edit':
        ret = this.editZone(arg1, arg2)
        break
      case 'travel_policy':
        ret = this.submitTravelPolicy(arg1, arg2)
        break
      case 'travel_policy_row_add':
        ret = this.addTravelPolicyRow(arg1)
        break
      case 'travellers_import':
        ret = this.importTravellers(arg1, arg2)
        break
      case 'mass_contingents_edit':
        ret = this.massContingentsEdit(arg1)
        break
      default:
        throw new Error('submitRequest function : type not allowed')
    }

    return ret
  }

  softDeleteUnfinishedBooking (id) {
    return axios.get(this.getApiEntry('/booking/softdelete/' + id), getAxiosConfig('delete_booking'))
  }

  submitNewPasswordRequest (values) {
    let apiUrl = values['email'] ? '/public/reset_user_password' : '/user/reset_password'
    return axios.post(this.getApiEntry(apiUrl), values, getAxiosConfig('reset_user_password'))
  }

  submitHotelNewPasswordRequest (values) {
    let apiUrl = '/public/reset_hotel_user_password'
    return axios.post(this.getApiEntry(apiUrl), values, getAxiosConfig('reset_user_password'))
  }

  submitCompanyAddDomain (values) {
    if (values) { return axios.post(this.getApiEntry('/company/add_domain'), values, getAxiosConfig('company_add_domain')) }
  }

  submitCompanyLogo (value) {
    return axios.post(this.getApiEntry('/company/logo'), value, getAxiosConfig('company_change_logo', 'application/form-data'))
  }

  submitAccountCreationRequest (values, companyUid) {
    let url = this.getApiEntry('/public/create_account')

    if (companyUid) url += `?company=${companyUid}`

    return axios.post(url, values, getAxiosConfig('create_account'))
  }

  submitGroupRequest (values) {
    let startDate = new Date(values.dates.from)
    let endDate = new Date(values.dates.to)
    let requestDate = new Date(values.requestDate)
    // Copy variable (not by reference)
    var newValues = JSON.parse(JSON.stringify(values))
    newValues.dates.from = formatDateForOnlineRequest(startDate)
    newValues.dates.to = formatDateForOnlineRequest(endDate)
    newValues.requestDate = formatDateForOnlineRequest(requestDate)
    return axios.post(this.getApiEntry('/groups/requests'), newValues, getAxiosConfig())
  }

  submitSeminarRequest (values) {
    let startDate = new Date(values.dates.from)
    let endDate = new Date(values.dates.to)
    let requestDate = new Date(values.requestDate)
    // Copy variable (not by reference)
    var newValues = JSON.parse(JSON.stringify(values))
    newValues.dates.from = formatDateForOnlineRequest(startDate)
    newValues.dates.to = formatDateForOnlineRequest(endDate)
    newValues.requestDate = formatDateForOnlineRequest(requestDate)
    return axios.post(this.getApiEntry('/seminars/requests'), newValues, getAxiosConfig())
  }

  submitHomeRequest (values, isOffline) {
    // No server validation
    let nullables = ['favorite', 'services', 'location', 'hotel']
    let errors = this.getRequiredFieldsErrors(values, nullables, isOffline)

    if (values['bedrooms']['single'] === 0 && values['bedrooms']['double'] === 0) {
      errors['bedrooms'] = errorCodes['CHOSE_ROOM']
    }

    if (values.dates.from > values.dates.to) {
      errors['dates'] = errorCodes['STARTING_DATE_AFTER_ENDING_DATE']
    }

    let success = Object.keys(errors).length === 0

    let response = {
      data: {
        success: success,
        errors: errors,
        data: values
      }
    }

    return new Promise(function (resolve, reject) {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    })
  }

  submitTravellerAdd (values, uniqueChain) {
    let url = (uniqueChain) ? '/public/traveller/create/' + uniqueChain : '/traveller/create'
    return axios.post(this.getApiEntry(url), values, getAxiosConfig('traveller_add'))
  }

  submitTravellerEdit (values) {
    return axios.post(this.getApiEntry('/traveller/' + values.id + '/edit'), values, getAxiosConfig())
  }

  submitOnlineRequest (search, filters, sort, show) {
    let values = {
      search: this.commonApi.parseSearchOnlineRequest(search),
      filters: filters,
      sort: sort,
      show: show
    }

    return axios.post(
      this.getApiEntry('/hrs/hotels/search_availabilities'),
      values,
      getAxiosConfig('hrs_hotels_search_availabilities')
    )
  }

  submitHotelDetailAvailabilitiesRequest (search) {
    return axios.post(
      this.getApiEntry('/hrs/hotels/hotel_detail_availabilities'),
      this.commonApi.parseSearchOnlineRequest(search),
      getAxiosConfig('hrs_hotel_detail_availabilities')
    )
  }

  submitOnlineBooking (values) {
    let startDate = new Date(values.dates.from)
    let endDate = new Date(values.dates.to)
    // Copy variable (not by reference)
    var newValues = JSON.parse(JSON.stringify(values))
    newValues.dates.from = formatDateForOnlineRequest(startDate)
    newValues.dates.to = formatDateForOnlineRequest(endDate)

    if (values.cancellationDeadline) {
      let deadline = new Date(values.cancellationDeadline)
      newValues.cancellationDeadline = formatDateTimeForOnlineRequest(deadline)
    }

    return axios.post(
      this.getApiEntry('/booking/online/submit'),
      newValues,
      getAxiosConfig('booking_online_create')
    )
  }

  validateOnlineBooking (values) {
    return axios.post(this.getApiEntry('/booking/online/validate'), values, getAxiosConfig('booking_online_validate'))
  }

  submitOfflineRequest (values) {
    return axios.post(
      this.getApiEntry('/booking/create'),
      this.commonApi.parseOfflineRequestValues(values),
      getAxiosConfig('booking_offline_create')
    )
  }

  submitGenericRequest (values, uniqueChain) {
    return axios.post(
      this.getApiEntry('/public/booking/generic/create?user=' + store.state.generic.user),
      this.commonApi.parseOfflineRequestValues(values, uniqueChain),
      getAxiosConfig('booking_create')
    )
  }

  submitPreloggedRequest (values, uniqueChain) {
    return axios.post(
      this.getApiEntry('/public/booking/create'),
      this.commonApi.parseOfflineRequestValues(values, uniqueChain),
      getAxiosConfig('booking_create')
    )
  }

  submitPublicOfflineBookingEdit (values, token) {
    return axios.post(this.getApiEntry('/public/booking/' + token + '/edit'), values, getAxiosConfig('public_offline_booking_edit'))
  }

  submitOfflineRequestEdit (values, bookingId) {
    return axios.post(this.getApiEntry('/booking/' + bookingId + '/edit'), values, getAxiosConfig('offline_booking_edit'))
  }

  submitOfflineRequestDuplicate (values) {
    return axios.post(this.getApiEntry('/booking/duplicate'), values, getAxiosConfig('offline_booking_duplicate'))
  }

  submitSurvey (values) {
    return axios.post(this.getApiEntry('/survey/create'), values, getAxiosConfig('survey_create'))
  }

  submitPublicSurvey (values, token) {
    return axios.post(this.getApiEntry('/public/survey/' + token + '/create'), values, getAxiosConfig('public_survey_create'))
  }

  submitBookingCancel (values, bookingId) {
    return axios.post(this.getApiEntry('/booking/' + bookingId + '/cancel'), values, getAxiosConfig('booking_cancel'))
  }

  submitPublicBookingCancel (values, token) {
    return axios.post(this.getApiEntry('/public/booking/' + token + '/cancel'), values, getAxiosConfig('public_booking_cancel'))
  }

  submitDenyBooking (values, bookingId) {
    let url = (values.isUnlogged) ? '/public/' : '/'
    return axios.post(this.getApiEntry(url + 'booking/' + bookingId + '/deny'), values, getAxiosConfig('booking_deny'))
  }

  submitAllowBooking (values, bookingId) {
    let url = (values.isUnlogged) ? '/public/' : '/'
    return axios.get(this.getApiEntry('/booking/' + bookingId + '/allow'), getAxiosConfig('booking_allow'))
  }

  submitConfirmBooking (bookingId) {
    return axios.get(this.getApiEntry('/booking/' + bookingId + '/confirm'), getAxiosConfig('booking_confirm'))
  }

  submitSendVoucher (email, bookingId) {
    return axios.post(
      this.getApiEntry('/booking/' + bookingId + '/send_voucher'),
      email,
      getAxiosConfig('send_voucher')
    )
  }

  submitNotifyTravellers (bookingId) {
    return axios.get(this.getApiEntry('/booking/' + bookingId + '/notify_travellers'),
      getAxiosConfig('notify_travellers')
    )
  }

  submitOrderNumber (formData, bookingId) {
    return axios.post(
      this.getApiEntry('/booking/' + bookingId + '/order_number'),
      formData,
      getAxiosConfig('order_number', 'application/form-data')
    )
  }

  submitResendEmail (email, maillogId) {
    return axios.post(
      this.getApiEntry('/maillog/' + maillogId + '/send'),
      email,
      getAxiosConfig('resend_email')
    )
  }

  getRequiredFieldsErrors (values, nullables, isOffline) {
    let errors = {}

    if (values.favorite.location || values.favorite.hotel) { nullables.push('destination') }

    if (values.favorite.hotel) {
      if (!values.favorite.hotel.hrs_ref && !isOffline) {
        errors['favorite'] = errorCodes['BAD_FAVORITE_HOTEL']
      }
    } else if (values.favorite.location) {
      if (!values.favorite.location.geo_lat && !isOffline) {
        errors['favorite'] = errorCodes['BAD_FAVORITE_LOCATION']
      }
    }

    for (let index in values) {
      if (values.hasOwnProperty(index)) {
        if (nullables.indexOf(index) === -1 && values[index] === null) {
          errors[index] = errorCodes['REQUIRED_FIELD']
        }

        if (values[index] !== null && typeof values[index] === 'object') {
          if (index === 'destination') {
            /*
            //values[index] = values[index]['locationId']
            if(values[index]['locationId'] === null) {
              errors[key] = errorCodes['REQUIRED_FIELD']
            }
            */
            // Warning: when we search an address, locationId is null but location is not empty
            if (values[index] === null) {
              errors[key] = errorCodes['REQUIRED_FIELD']
            }
          } else {
            for (let key in values[index]) {
              if (values[index].hasOwnProperty(key)) {
                let val = values[index][key]

                if (nullables.indexOf(key) === -1 && val === null) {
                  errors[key] = errorCodes['REQUIRED_FIELD']
                }
              }
            }
          }
        }
      }
    }
    return errors
  }

  addZone (values) {
    return axios.post(
      this.getApiEntry('/zone/add'),
      values,
      getAxiosConfig('add_zone')
    )
  }

  editZone (id, values) {
    return axios.post(
      this.getApiEntry('/zone/' + id + '/edit'),
      values,
      getAxiosConfig('add_zone')
    )
  }

  submitTravelPolicy (companyId, values) {
    return axios.post(
      this.getApiEntry('/company/' + companyId + '/travel_policy'),
      values,
      getAxiosConfig('submit_travel_policy')
    )
  }

  addTravelPolicyRow (values) {
    return axios.post(
      this.getApiEntry('/travel_policy_row/add'),
      values,
      getAxiosConfig('add_travel_policy_row')
    )
  }

  importTravellers (formData, companyId) {
    let uri = '/travellers/import/'
    if (companyId) { uri += companyId }
    return axios.post(
      this.getApiEntry().concat(uri),
      formData,
      getAxiosConfig('travellers_import', 'application/form-data')
    )
  }

  submitWhiteMark (companyId, values) {
    return axios.post(
      this.getApiEntry('/company/' + companyId + '/fields_white_mark'),
      values,
      getAxiosConfig('submit_white_mark')
    )
  }

  massContingentsEdit (values) {
    return axios.post(
      this.getApiEntry('/contingents/mass-edit'),
      values,
      getAxiosConfig('mass_contingents_edit')
    )
  }
}
